import React from 'react';
import { MapPin, Phone, Clock, Navigation, Instagram, MessageCircle } from 'lucide-react';
import { businessInfo } from '../data/mockData';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const Visit = () => {
  const mapEmbedUrl =
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3024.734448819935!2d19.961926976438416!3d40.70184573823659!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x135a99c6aee60e13%3A0xc95673340cc19314!2sLor95Boutique!5e0!3m2!1sen!2s!4v1787563688690!5m2!1sen!2s";

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Vizitoni Dyqanin në Berat"
        description="Vizitoni Lor95 Boutique në Rruga Desaret, Berat. Dyqani është i hapur çdo ditë nga ora 09:00 deri në 21:00."
        path="/visit"
      />

      {/* Seksioni Kryesor */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">

        <div className="absolute inset-0 z-0">
          <img 
            src={businessInfo.storefrontImage}
            alt="Vizitoni Lor95 Boutique"
            className="w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-4">

          <MapPin className="h-16 w-16 mx-auto mb-6 text-white animate-in fade-in zoom-in duration-700" />

          <h1 className="text-5xl sm:text-6xl font-serif font-bold text-white mb-4 animate-in fade-in slide-in-from-bottom duration-700">
            Na Vizitoni
          </h1>

          <p className="text-xl text-white/90 animate-in fade-in slide-in-from-bottom duration-700 delay-150">
            Na gjeni në zemër të qytetit të Beratit
          </p>

        </div>
      </section>


      {/* Informacioni i Vendndodhjes */}
      <section className="py-20 bg-gray-50">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Karta e Adresës */}
            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 animate-in fade-in slide-in-from-bottom">

              <CardContent className="p-8 text-center">

                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-900 text-white mb-6">
                  <MapPin className="h-8 w-8" />
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Adresa Jonë
                </h3>

                <p className="text-gray-600 leading-relaxed mb-2">
                  {businessInfo.address}
                </p>

                <p className="text-sm text-gray-500">
                  {businessInfo.locationCode}
                </p>

              </CardContent>

            </Card>


            {/* Karta e Orarit */}
            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 animate-in fade-in slide-in-from-bottom delay-150">

              <CardContent className="p-8 text-center">

                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-900 text-white mb-6">
                  <Clock className="h-8 w-8" />
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Orari i Punës
                </h3>

                <p className="text-gray-600 leading-relaxed mb-2">
                  Hapur çdo ditë
                </p>

                <p className="text-lg font-semibold text-gray-900">
                  Mbyllet në orën 21:00
                </p>

              </CardContent>

            </Card>


            {/* Karta e Kontaktit */}
            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 animate-in fade-in slide-in-from-bottom delay-300">

              <CardContent className="p-8 text-center">

                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-900 text-white mb-6">
                  <Phone className="h-8 w-8" />
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Na Kontaktoni
                </h3>

                <a 
                  href={`tel:${businessInfo.phone}`}
                  className="text-lg text-gray-600 hover:text-gray-900 transition-colors mb-4 block"
                >
                  {businessInfo.phone}
                </a>

                <div className="flex flex-col items-center gap-3">
                  <a 
                    href={businessInfo.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <Instagram className="h-5 w-5" />
                    <span>@lor95boutique</span>
                  </a>

                  <a
                    href="https://wa.me/355693520111"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <MessageCircle className="h-5 w-5" />
                    <span>WhatsApp</span>
                  </a>
                </div>

              </CardContent>

            </Card>

          </div>
        </div>
      </section>


      {/* Seksioni i Hartës */}
      <section className="py-20 bg-white">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center mb-12">

            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">
              Na Gjeni në Hartë
            </h2>

            <p className="text-lg text-gray-600">
              Të vendosur në qytetin e bukur të Beratit, Shqipëri
            </p>

          </div>


          <div className="relative w-full h-[500px] rounded-lg overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-700">

            <iframe
              src={mapEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Vendndodhja e Lor95 Boutique"
            ></iframe>

          </div>


          <div className="text-center mt-8">

            <Button 
              asChild
              size="lg"
              className="bg-gray-900 hover:bg-gray-800 text-white px-8 rounded-none"
            >

              <a 
                href="https://maps.app.goo.gl/KNEhbnu6hkvZpY7z8"
                target="_blank"
                rel="noopener noreferrer"
              >

                <Navigation className="mr-2 h-5 w-5" />

                Merr Udhëzimet

              </a>

            </Button>

          </div>

        </div>
      </section>


      {/* Seksioni i Imazheve të Butikut */}
      <section className="py-20 bg-gray-50">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center mb-12">

            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">
              Përjetoni Lor95 Boutique
            </h2>

            <p className="text-lg text-gray-600">
              Hyni në botën tonë të modës elegante dhe moderne
            </p>

          </div>


          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

            {/* Butiku */}
            <div className="relative aspect-[4/3] overflow-hidden shadow-xl group animate-in fade-in slide-in-from-left duration-700">

              <img 
                src={businessInfo.storefrontImage}
                alt="Ambientet e jashtme të Lor95 Boutique"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

              <div className="absolute bottom-6 left-6 right-6 text-white">

                <h3 className="text-2xl font-semibold mb-2">
                  Butiku Ynë
                </h3>

                <p className="text-white/90">
                  Mirë se vini në Lor95 Boutique në Berat
                </p>

              </div>

            </div>


            {/* Koleksionet */}
            <div className="relative aspect-[4/3] overflow-hidden shadow-xl group animate-in fade-in slide-in-from-right duration-700">

              <img 
                src={businessInfo.clothingRacksImage}
                alt="Koleksionet e Modës"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

              <div className="absolute bottom-6 left-6 right-6 text-white">

                <h3 className="text-2xl font-semibold mb-2">
                  Koleksione të Përzgjedhura
                </h3>

                <p className="text-white/90">
                  Zbuloni stilin perfekt për ju
                </p>

              </div>

            </div>

          </div>
        </div>
      </section>


      {/* Pse të Na Vizitoni */}
      <section className="py-20 bg-white">

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center mb-12">

            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">
              Pse të Vizitoni Lor95 Boutique?
            </h2>

          </div>


          <div className="space-y-6 text-lg text-gray-700 leading-relaxed">

            {/* Stilimi i Personalizuar */}
            <div className="bg-gray-50 p-6 border-l-4 border-gray-900">

              <h3 className="font-semibold text-gray-900 mb-2">
                Stilim i Personalizuar
              </h3>

              <p>
                Ekipi ynë me përvojë ju ofron vëmendje të personalizuar për t'ju ndihmuar
                të gjeni veshjet dhe kombinimet që përshtaten në mënyrë perfekte me stilin,
                personalitetin dhe preferencat tuaja.
              </p>

            </div>


            {/* Cilësi që Mund ta Përjetoni */}
            <div className="bg-gray-50 p-6 border-l-4 border-gray-900">

              <h3 className="font-semibold text-gray-900 mb-2">
                Cilësi që Mund ta Përjetoni
              </h3>

              <p>
                Zbuloni nga afër cilësinë e koleksioneve tona. Prekni materialet, provoni
                modelet dhe gjeni veshjet që ju bëjnë të ndiheni rehat, elegant dhe me
                vetëbesim.
              </p>

            </div>


            {/* Oferta Ekskluzive */}
            <div className="bg-gray-50 p-6 border-l-4 border-gray-900">

              <h3 className="font-semibold text-gray-900 mb-2">
                Oferta Ekskluzive në Butik
              </h3>

              <p>
                Na vizitoni për të zbuluar oferta dhe promocione të veçanta në butik,
                si dhe për të qenë ndër të parët që shihni modelet dhe koleksionet tona
                më të reja për femra dhe meshkuj.
              </p>

            </div>

          </div>


          <div className="text-center mt-12">

            <p className="text-xl text-gray-600 mb-6">
              Mezi presim t'ju mirëpresim!
            </p>

            <Button
              asChild
              size="lg"
              className="bg-gray-900 hover:bg-gray-800 text-white px-8 rounded-none"
            >

              <a href="/contact">
                Na Kontaktoni
              </a>

            </Button>

          </div>

        </div>
      </section>

    </div>
  );
};

export default Visit;
